import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Badge, Spinner, Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { loadingAllRole, createRole, updateRole, deleteRole, getRoleById } from '../../../api/role';
import { Shield, Plus, Edit, Trash2, RefreshCw } from 'react-feather';

const RoleManagement = () => {
  const [roleData, setRoleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    value: ''
  });

  useEffect(() => {
    fetchRoleData();
  }, []);

  const fetchRoleData = async () => {
    try {
      setLoading(true);
      setError(null);

      const roles = await loadingAllRole();
      setRoleData(roles);
    } catch (err) {
      console.error('❌ Error loading role data:', err);
      setError(err.message || 'Không thể tải dữ liệu role');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddRole = async () => {
    try {
      if (!formData.code || !formData.value) {
        alert('Vui lòng điền đầy đủ thông tin');
        return;
      }

      await createRole(formData);
      setModal(false);
      setFormData({ code: '', value: '' });
      await fetchRoleData();
      alert('Thêm role thành công!');
    } catch (err) {
      console.error('❌ Error adding role:', err);
      alert('Lỗi khi thêm role: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditRole = async () => {
    try {
      if (!selectedRole || !formData.code || !formData.value) {
        alert('Vui lòng điền đầy đủ thông tin');
        return;
      }

      await updateRole(selectedRole.id, formData);
      setEditModal(false);
      setSelectedRole(null);
      setFormData({ code: '', value: '' });
      await fetchRoleData();
      alert('Cập nhật role thành công!');
    } catch (err) {
      console.error('❌ Error editing role:', err);
      alert('Lỗi khi cập nhật role: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteRole = async () => {
    try {
      if (!selectedRole) {
        alert('Không tìm thấy role để xóa');
        return;
      }

      await deleteRole(selectedRole.id);
      setDeleteModal(false);
      setSelectedRole(null);
      await fetchRoleData();
      alert('Xóa role thành công!');
    } catch (err) {
      console.error('❌ Error deleting role:', err);
      alert('Lỗi khi xóa role: ' + (err.message || 'Unknown error'));
    }
  };

  const openEditModal = (role) => {
    setSelectedRole(role);
    setFormData({
      code: role.code || '',
      value: role.value || ''
    });
    setEditModal(true);
  };

  const openDeleteModal = (role) => {
    setSelectedRole(role);
    setDeleteModal(true);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setFormData({ code: '', value: '' });
    }
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      setSelectedRole(null);
      setFormData({ code: '', value: '' });
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (!deleteModal) {
      setSelectedRole(null);
    }
  };

  const getRoleBadgeColor = (code) => {
    const colorMap = {
      'R1': 'danger',
      'R2': 'warning',
      'R3': 'success',
      'R4': 'info',
      'SUPER_ADMIN': 'danger',
      'ADMIN': 'warning'
    };
    return colorMap[code] || 'secondary';
  };

  if (loading) {
    return (
      <Col xxl="6" className="box-col-6">
        <Card>
          <CardBody className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="text-center">
              <Spinner color="primary" />
              <p className="mt-2 mb-0">Đang tải dữ liệu role...</p>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  if (error) {
    return (
      <Col xxl="6" className="box-col-6">
        <Card>
          <CardBody>
            <Alert color="danger">
              <strong>Lỗi:</strong> {error}
            </Alert>
            <Button color="primary" size="sm" onClick={fetchRoleData}>
              Thử lại
            </Button>
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <Col xxl="6" className="box-col-6">
      <Card>
        <CardHeader className="card-no-border d-flex justify-content-between align-items-center">
          <H5>
            <Shield size={20} className="me-2" />
            Quản Lý Role
          </H5>
          <div className="d-flex gap-2">
            <Button color="outline-primary" size="sm" onClick={fetchRoleData}>
              <RefreshCw size={16} className="me-1" />
              Làm mới
            </Button>
            <Button color="primary" size="sm" onClick={toggleModal}>
              <Plus size={16} className="me-1" />
              Thêm Role
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          {roleData.length === 0 ? (
            <Alert color="info" className="mb-0">
              <small>Chưa có role nào. Nhấn "Thêm Role" để tạo mới.</small>
            </Alert>
          ) : (
            <>
              <div className="role-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {roleData.map((role) => (
                  <div key={role.id} className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                    <div className="role-info flex-grow-1">
                      <div className="d-flex align-items-center gap-2">
                        <Badge color={getRoleBadgeColor(role.code)} style={{ fontSize: '0.75rem', padding: '0.35em 0.65em' }}>
                          {role.code}
                        </Badge>
                        <span className="f-w-500" style={{ fontSize: '15px' }}>{role.value}</span>
                      </div>
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0">
                      <Button 
                        color="outline-warning" 
                        size="sm" 
                        onClick={() => openEditModal(role)}
                        title="Sửa"
                      >
                        <Edit size={14} />
                      </Button>
                      <Button 
                        color="outline-danger" 
                        size="sm" 
                        onClick={() => openDeleteModal(role)}
                        title="Xóa"
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-muted small">
                Tổng số: {roleData.length} role
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Modal Thêm Role */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Thêm Role Mới</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Code Role *</Label>
            <Input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="Ví dụ: R4, SUPER_ADMIN"
            />
            <small className="text-muted">Mã định danh cho role (ví dụ: R1, R2, ADMIN)</small>
          </FormGroup>
          <FormGroup>
            <Label>Tên Role *</Label>
            <Input
              type="text"
              name="value"
              value={formData.value}
              onChange={handleInputChange}
              placeholder="Ví dụ: Admin AppQuiz"
            />
            <small className="text-muted">Tên hiển thị của role</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>Hủy</Button>
          <Button color="primary" onClick={handleAddRole}>Thêm Role</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Sửa Role */}
      <Modal isOpen={editModal} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Sửa Role</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Code Role *</Label>
            <Input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="Ví dụ: R4, SUPER_ADMIN"
            />
            <small className="text-muted">Mã định danh cho role</small>
          </FormGroup>
          <FormGroup>
            <Label>Tên Role *</Label>
            <Input
              type="text"
              name="value"
              value={formData.value}
              onChange={handleInputChange}
              placeholder="Ví dụ: Admin AppQuiz"
            />
            <small className="text-muted">Tên hiển thị của role</small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditModal}>Hủy</Button>
          <Button color="warning" onClick={handleEditRole}>Cập Nhật</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Xóa Role */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Xác Nhận Xóa</ModalHeader>
        <ModalBody>
          <p>Bạn có chắc chắn muốn xóa role <strong>{selectedRole?.value}</strong> (Code: {selectedRole?.code})?</p>
          <Alert color="warning">
            <strong>Cảnh báo:</strong> Hành động này không thể hoàn tác! Nếu có admin đang sử dụng role này, có thể gây lỗi hệ thống.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>Hủy</Button>
          <Button color="danger" onClick={handleDeleteRole}>Xóa</Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
};

export default RoleManagement;

